import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, FileSpreadsheet } from "lucide-react";
import toast from "react-hot-toast";
import {
  useJournalEntries,
  usePostJournalEntry,
  useVoidJournalEntry,
  useDeleteJournalEntry,
  JOURNAL_ENTRY_STATUS_LABEL,
  JournalEntryStatus,
  journalEntryClientName,
  journalEntryTotal,
} from "@/api/accounting";
import { useAuthStore } from "@/stores/authStore";
import { getApiErrorMessage } from "@/api/client";
import { downloadFile } from "@/utils/downloadFile";
import CreateJournalEntryModal from "./CreateJournalEntryModal";

const STATUS_STYLE: Record<JournalEntryStatus, string> = {
  BORRADOR: "bg-gray-100 text-gray-600",
  CONTABILIZADO: "bg-green-100 text-green-700",
  ANULADO: "bg-red-100 text-red-700",
};

const STATUS_OPTIONS = Object.keys(JOURNAL_ENTRY_STATUS_LABEL) as JournalEntryStatus[];

export default function JournalEntriesPage() {
  const [status, setStatus] = useState<JournalEntryStatus | "">("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { data, isLoading } = useJournalEntries({
    status: status || undefined,
    search: search || undefined,
    page,
    pageSize: 15,
  });
  const postEntry = usePostJournalEntry();
  const voidEntry = useVoidJournalEntry();
  const deleteEntry = useDeleteJournalEntry();

  const entries = data?.data ?? [];
  const meta = data?.meta;

  const handlePost = async (id: string) => {
    try {
      await postEntry.mutateAsync(id);
      toast.success("Asiento contabilizado.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleVoid = async (id: string) => {
    if (!window.confirm("¿Anular este asiento? Esta acción no revierte los saldos automáticamente.")) return;
    try {
      await voidEntry.mutateAsync(id);
      toast.success("Asiento anulado.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("¿Eliminar este asiento en borrador?")) return;
    try {
      await deleteEntry.mutateAsync(id);
      toast.success("Asiento eliminado.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleExportExcel = async () => {
    try {
      await downloadFile("/accounting/entries/export.xlsx", "asientos-contables.xlsx", { status: status || undefined, search: search || undefined });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Asientos contables</h1>
          <p className="text-sm text-gray-500">Partida doble: borrador → contabilizado, con anulación en lugar de borrado una vez contabilizado.</p>
        </div>
        <div className="flex gap-2">
          {hasPermission("accounting.export") && (
            <button type="button" className="btn-secondary" onClick={handleExportExcel}>
              <FileSpreadsheet size={16} className="mr-1.5" /> Exportar Excel
            </button>
          )}
          {hasPermission("accounting.create") && (
            <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
              <Plus size={16} className="mr-1.5" /> Nuevo asiento
            </button>
          )}
        </div>
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <input
          type="search"
          placeholder="Buscar por descripción..."
          className="input-field min-w-[220px] flex-1"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="input-field w-auto"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as JournalEntryStatus | "");
            setPage(1);
          }}
        >
          <option value="">Todos los estados</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {JOURNAL_ENTRY_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">N.°</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Fecha</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Descripción</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Cliente</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Valor</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && entries.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                  No hay asientos que coincidan con los filtros.
                </td>
              </tr>
            )}
            {entries.map((entry) => (
              <tr key={entry.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-gray-500">{entry.entryNumber}</td>
                <td className="px-4 py-3 text-gray-600">{entry.entryDate}</td>
                <td className="px-4 py-3 font-medium text-gray-900">{entry.description}</td>
                <td className="px-4 py-3 text-gray-600">
                  {entry.client ? (
                    <Link to={`/clients/${entry.client.id}`} className="hover:text-brand-600">
                      {journalEntryClientName(entry)}
                    </Link>
                  ) : (
                    <span className="text-gray-400">Interno</span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(
                    journalEntryTotal(entry)
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[entry.status]}`}>
                    {JOURNAL_ENTRY_STATUS_LABEL[entry.status]}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  {entry.status === "BORRADOR" && hasPermission("accounting.update") && (
                    <button type="button" className="mr-2 text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => handlePost(entry.id)}>
                      Contabilizar
                    </button>
                  )}
                  {entry.status === "BORRADOR" && hasPermission("accounting.delete") && (
                    <button type="button" className="text-xs font-medium text-red-500 hover:text-red-700" onClick={() => handleDelete(entry.id)}>
                      Eliminar
                    </button>
                  )}
                  {entry.status === "CONTABILIZADO" && hasPermission("accounting.update") && (
                    <button type="button" className="text-xs font-medium text-red-500 hover:text-red-700" onClick={() => handleVoid(entry.id)}>
                      Anular
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {meta && meta.totalPages && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm">
            <span className="text-gray-500">
              Página {meta.page} de {meta.totalPages} ({meta.total} asientos)
            </span>
            <div className="space-x-2">
              <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </button>
              <button
                className="btn-secondary"
                disabled={!!meta.totalPages && page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {showCreate && <CreateJournalEntryModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
